import type { CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Flame } from '@sorare/core/src/atoms/icons/Flame';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM, BrandS, BrandXS } from '@sorare/core/src/atoms/typography';
import { ORIGIN } from '@sorare/core/src/config';
import {
  ANY_SPORT_MISSIONS_CATEGORY_RARITY,
  BLOG_SPORT,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import cards from './assets/cards.webp';
import missionRewards from './assets/missionRewards.webp';
import texture from './assets/texture.webp';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--unit);
  width: 100%;

  @media ${laptopAndAbove} {
    flex-direction: row;
    width: auto;
  }
`;
const SecondaryTilesWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: var(--unit);

  @media ${laptopAndAbove} {
    flex-direction: column;
  }
`;
const Tile = styled(ButtonBase)`
  border-radius: var(--unit);
  background:
    no-repeat center / cover url(${texture}),
    var(--c-black);
`;

const PrimaryTile = styled(Tile)`
  position: relative;
  width: 100%;
  height: 200px;
  display: flex;
  align-items: flex-end;
  overflow: hidden;
  isolation: isolate;

  @media ${laptopAndAbove} {
    width: auto;
    min-width: 268px;
    height: 268px;
  }
`;
const SecondaryTile = styled(Tile)`
  position: relative;
  height: 130px;
  padding: var(--double-unit) var(--unit);
  width: calc(50% - var(--half-unit));
  display: flex;
  align-items: flex-end;

  @media ${laptopAndAbove} {
    width: auto;
    min-width: calc(40 * var(--unit));
    height: 100%;
    padding: var(--double-unit);
  }
`;
const PlayerCardsImage = styled.img`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: calc(-1 * var(--triple-unit));
  width: calc(24 * var(--unit));
  height: calc(24 * var(--unit));
  object-fit: contain;

  @media ${laptopAndAbove} {
    bottom: auto;
    top: var(--double-unit);
  }
`;
const BlurEffect = styled.div`
  position: absolute;
  height: calc(15 * var(--unit));
  left: 0;
  bottom: 0;
  right: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.8) 74.8%
  );
  mask: linear-gradient(180deg, transparent 0%, black 100%);
  backdrop-filter: blur(4px);
`;
const TitleWrapper = styled(Vertical)`
  z-index: 1;
  width: 100%;
  padding: 0 var(--unit) var(--triple-unit) var(--unit);
  justify-content: flex-end;
`;
const ResponsiveBrandXS = styled(BrandXS)`
  @media ${laptopAndAbove} {
    --typography-font: var(--t-brand-s);
  }
`;
const ResponsiveBrandS = styled(BrandS)`
  @media ${laptopAndAbove} {
    --typography-font: var(--t-brand-m);
  }
`;
const SecondaryIconWrapper = styled.div`
  position: absolute;
  top: var(--double-unit);
  left: 50%;
  transform: translateX(-50%);

  @media ${laptopAndAbove} {
    top: 50%;
    right: var(--double-unit);
    left: auto;
    transform: translateY(-50%);
  }

  &::before {
    content: '';
    position: absolute;
    inset: calc(-1 * var(--unit)) calc(-1 * var(--quadruple-unit));
    background: var(--blur-effect-color);
    border-radius: 50%;
    filter: blur(20px);
    opacity: 0.2;

    @media ${laptopAndAbove} {
      inset: 0 calc(-1 * var(--double-unit)) calc(-1 * var(--unit)) 0;
    }
  }
`;
const RewardsImage = styled.img`
  width: calc(16 * var(--unit));
  object-fit: contain;
`;

export const SpecialEventPromoTiles = () => {
  const isLaptopAndAbove = useIsLaptop();
  const { generateSportContextPath } = useSportContext();
  const { postMessage, isIosApp } = useIsMobileApp();

  return (
    <Root>
      <PrimaryTile
        to={isIosApp ? undefined : generateSportContextPath(BLOG_SPORT)}
        onClick={() => {
          if (isIosApp) {
            postMessage('openBlog', {
              url: `${ORIGIN}${generateSportContextPath(BLOG_SPORT)}`,
            });
          }
        }}
      >
        <PlayerCardsImage src={cards} />
        {!isLaptopAndAbove && <BlurEffect />}
        <TitleWrapper>
          <BrandS color="var(--c-rewards-blue)">
            <FormattedMessage {...glossary.new} />
          </BrandS>
          <BrandM>
            <FormattedMessage
              id="commonCard.SpecialEventPromoTiles.primaryTile.title"
              defaultMessage="Special Editions"
            />
          </BrandM>
        </TitleWrapper>
      </PrimaryTile>
      <SecondaryTilesWrapper>
        <SecondaryTile
          to={
            isIosApp
              ? undefined
              : generateSportContextPath(ANY_SPORT_MISSIONS_CATEGORY_RARITY, {
                  params: {
                    category: 'play',
                    rarity: 'common',
                  },
                })
          }
          onClick={() => {
            if (isIosApp) {
              postMessage('openMissions', {
                category: 'play',
                rarity: 'common',
              });
            }
          }}
        >
          <SecondaryIconWrapper
            style={
              {
                '--blur-effect-color': 'var(--c-rewards-orange)',
              } as CSSProperties
            }
          >
            <Flame size={8} />
          </SecondaryIconWrapper>
          <Vertical>
            <ResponsiveBrandXS color="var(--c-rewards-blue)">
              <FormattedMessage
                id="commonCard.SpecialEventPromoTiles.secondaryTile.subTitle"
                defaultMessage="$ cash"
              />
            </ResponsiveBrandXS>
            <ResponsiveBrandS>
              <FormattedMessage {...fantasy.hotStreaks} />
            </ResponsiveBrandS>
          </Vertical>
        </SecondaryTile>
        <SecondaryTile
          to={
            isIosApp
              ? undefined
              : generateSportContextPath(ANY_SPORT_MISSIONS_CATEGORY_RARITY, {
                  params: {
                    category: 'play',
                    rarity: 'common',
                  },
                })
          }
          onClick={() => {
            if (isIosApp) {
              postMessage('openMissions', {
                category: 'play',
                rarity: 'common',
              });
            }
          }}
        >
          <SecondaryIconWrapper
            style={
              {
                '--blur-effect-color': 'var(--c-common)',
              } as CSSProperties
            }
          >
            <RewardsImage src={missionRewards} />
          </SecondaryIconWrapper>
          <ResponsiveBrandS>
            <FormattedMessage
              id="commonCard.SpecialEventPromoTiles.secondaryTile.title2"
              defaultMessage="Special missions"
            />
          </ResponsiveBrandS>
        </SecondaryTile>
      </SecondaryTilesWrapper>
    </Root>
  );
};
